import {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {AddInviteesCardProps} from './types';
import {useTranslation} from 'react-i18next';
import {TransitionGroup} from 'react-transition-group';
import uniqBy from 'lodash/uniqBy';

import {
  getMaxGroupWorkspaceInvitees,
  searchGroupWorkspaceInvitees,
  searchMultipleGroupWorkspaceInvitees,
  useAppSelector,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {findByEmail, useDebounce} from '@lib/utils';

import {useAppDispatch, useModal, useScrollParentForChild} from '@hooks';

import {ChipSelect, InviteeCardCell} from '@molecules';
import {AddWorkspaceInviteesCardHint, ModalPage, ModalPageBottom} from '@organisms';
import {FlexCol} from '@quarks';
import {AddGroupMembersSection} from './AddGroupMembersSection';
import {PersonLike} from '@lib/infrastructure';

export const AddWorkspaceInviteesCard = ({
  onClose,
  onSave,
  passedInvitees,
  date,
  initialGroupId,
}: AddInviteesCardProps) => {
  const {t} = useTranslation();
  const {removePages} = useModal();
  const [searchTerm, setSearchTerm] = useState('');
  const maxGroupWorkspaceInvitees = useAppSelector(getMaxGroupWorkspaceInvitees);
  const debouncedSearchTerm = useDebounce(searchTerm, 600);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(initialGroupId || null);

  const [isSearching, setIsSearching] = useState(false);
  const [resultsArray, setResultsArray] = useState<PersonLike[]>([]);
  const [selectedInvitee, setSelectedInvitee] = useState<number | undefined>(undefined);
  const [invitees, setInvitees] = useState<PersonLike[]>(passedInvitees);
  const dispatch = useAppDispatch();
  const [scrollRef, childWrapperRef] = useScrollParentForChild(selectedInvitee);

  const reachedMaxSelection = invitees.length >= maxGroupWorkspaceInvitees;

  const showHint = searchTerm.length === 0 && debouncedSearchTerm.length >= 1;
  const showGroupMembersSection = searchTerm.length === 0 && debouncedSearchTerm.length === 0;

  const handleMultiSearch = useCallback(
    async (query: string[]) => {
      const result = await dispatch(withAsyncThunkErrorHandling(() => searchMultipleGroupWorkspaceInvitees(query)));
      if (result.success) {
        setInvitees((prevInvitees) => {
          const uniqueInvitees = uniqBy([...prevInvitees, ...result.result], 'id');

          return uniqueInvitees.splice(0, maxGroupWorkspaceInvitees);
        });
      }

      setSearchTerm('');
    },
    [dispatch, maxGroupWorkspaceInvitees],
  );

  const handleSingleSearch = useCallback(
    async (query: string) => {
      const result = await dispatch(withAsyncThunkErrorHandling(() => searchGroupWorkspaceInvitees(query)));

      if (result.success) {
        setResultsArray(result.result);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const handleSearch = async () => {
      const splitRegex = /[,|;|\|]\s{0,}/; // split on , ; |
      const queryStrings = debouncedSearchTerm.split(splitRegex).filter(Boolean);
      const isMultiSearch = queryStrings.length > 1;

      if (isMultiSearch) {
        await handleMultiSearch(queryStrings);
      } else {
        await handleSingleSearch(queryStrings[0]);
      }

      setIsSearching(false);
    };

    if (debouncedSearchTerm.length >= 2) handleSearch();
    if (debouncedSearchTerm.length < 2) {
      setResultsArray([]);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, handleSingleSearch, handleMultiSearch]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length >= 2) {
      setResultsArray([]);
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
    setSearchTerm(e.target.value);
  };

  const filteredResultsArray =
    resultsArray.length > 0 ? resultsArray.filter((result) => !findByEmail(invitees, result.email)) : [];

  const handleOnClickInvitee = (user: PersonLike) => {
    setInvitees((prevInitees) => [...prevInitees, user]);
    setSearchTerm('');
    setResultsArray([]);
  };

  const handleGroupSelect = (groupMembersAsInvitees: PersonLike[], groupId: string) => {
    setInvitees((prevInvitees) => {
      const uniqueInvitees = uniqBy([...prevInvitees, ...groupMembersAsInvitees], 'id');

      return uniqueInvitees.splice(0, maxGroupWorkspaceInvitees);
    });
    setSelectedGroupId(groupId);
  };

  if (selectedGroupId && invitees.length === 0) {
    setSelectedGroupId(null);
  }

  return (
    <ModalPage
      bottom={
        <ModalPageBottom
          buttonLabel={t('translation:Confirm')}
          onClick={() => {
            onSave(invitees, selectedGroupId);
            removePages(1);
          }}
        />
      }
      onBack={() => removePages(1)}
      onClose={onClose}
      ref={scrollRef}
      title={t('screen:Invitees')}>
      <FlexCol gap={16}>
        <ChipSelect
          isOutsideInviteesAllowed={false}
          reachedMaxSelection={reachedMaxSelection}
          isLoading={isSearching}
          onChange={handleChange}
          optionsArray={filteredResultsArray}
          placeholder={t('translation:SearchInviteePlaceholder')}
          isPlaceholderTransparent={!!invitees.length}
          selectedOptionIndex={selectedInvitee}
          selection={invitees}
          setSelectedOptionIndex={setSelectedInvitee}
          setSelection={setInvitees}
          setValue={setSearchTerm}
          value={searchTerm}
        />

        <hr />

        <div className="grid grid-cols-1 gap-4">
          <AddWorkspaceInviteesCardHint
            reachedMaxSelection={reachedMaxSelection}
            debouncedSearchTerm={debouncedSearchTerm}
            isSearching={isSearching}
            show={showHint}
            length={resultsArray.length}
            searchTerm={searchTerm}
          />
          <FlexCol columnGap={8}>
            <AddGroupMembersSection
              onSelect={handleGroupSelect}
              show={showGroupMembersSection}
              dateKey={date}
            />

            <div ref={childWrapperRef}>
              <TransitionGroup component={null}>
                {filteredResultsArray.map((user, i) => (
                  <InviteeCardCell
                    key={user.id}
                    i={i}
                    length={filteredResultsArray.length}
                    onClick={() => handleOnClickInvitee(user)}
                    selected={selectedInvitee === i}
                    user={user}
                  />
                ))}
              </TransitionGroup>
            </div>
          </FlexCol>
        </div>
      </FlexCol>
    </ModalPage>
  );
};
